import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Database, Layers, MessageSquare, BarChart3, ArrowRight, Sparkles, X, Loader2, Check, ArrowUp, Plus, ChevronUp, ChevronRight, ChevronDown, Bot, Shuffle, Mic, Network, Target, Rocket, DollarSign, Users, Circle, CheckCircle2, FileText } from 'lucide-react';
import AnimatedSemanticGraph from '../components/AnimatedSemanticGraph';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import GridBackground from '../components/GridBackground';

// Animated Counter Component
const AnimatedCounter = ({ end, suffix = '', duration = 2000, icon: Icon, delay = 0 }) => {
  const [count, setCount] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const currentRef = counterRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          } else {
            // Reset when out of view so it animates again
            setIsVisible(false);
            setHasStarted(false);
            setCount(null);
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible && !hasStarted) {
      // Add delay for staggered animation
      const delayTimeout = setTimeout(() => {
        setHasStarted(true);
        let startTime;
        const animate = (currentTime) => {
          if (!startTime) startTime = currentTime;
          const progress = Math.min((currentTime - startTime) / duration, 1);
          // Easing function for smooth animation
          const easeOutQuart = 1 - Math.pow(1 - progress, 4);
          setCount(Math.floor(easeOutQuart * end));
          if (progress < 1) {
            requestAnimationFrame(animate);
          }
        };
        requestAnimationFrame(animate);
      }, delay);

      return () => clearTimeout(delayTimeout);
    }
  }, [isVisible, hasStarted, end, duration, delay]);

  return (
    <div ref={counterRef} className="flex items-center gap-2">
      <div className="w-[36px] h-[36px] rounded-lg bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-all duration-300 flex-shrink-0">
        <Icon className="w-[18px] h-[18px] text-blue-400" />
      </div>
      <span className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
        {count === null ? '--' : `${count}${suffix}`}
      </span>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionSuccess, setConnectionSuccess] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [displayedPlaceholder, setDisplayedPlaceholder] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState({ id: 'data-analysis', name: 'Data Analysis', icon: '🔍' });
  const [selectedModel, setSelectedModel] = useState({ id: 'gpt4', name: 'GPT-4o', provider: 'OpenAI' });
  const [selectedUseCase, setSelectedUseCase] = useState('manufacturing');
  const [showCreateSubscriptionModal, setShowCreateSubscriptionModal] = useState(false);
  const [newSubscriptionName, setNewSubscriptionName] = useState('');
  const [customUseCases, setCustomUseCases] = useState([]);
  const [formData, setFormData] = useState({
    connectionName: '',
    organizationName: '',
    accountName: '',
    warehouse: '',
    database: '',
    user: '',
    password: ''
  });

  const defaultUseCases = [
    { 
      id: 'manufacturing', 
      name: 'Manufacturing', 
      icon: '🏭',
      description: 'Industrial & Production Analytics',
      data: 'Production lines, equipment sensors, quality metrics, supply chain data',
      hint: 'Pre-configured with sample data to explore'
    },
    { 
      id: 'healthcare', 
      name: 'Healthcare', 
      icon: '🏥',
      description: 'Medical & Patient Insights',
      data: 'Patient records, clinical trials, medical devices, hospital operations',
      hint: 'Pre-configured with sample data to explore'
    },
    { 
      id: 'cpg', 
      name: 'CPG', 
      icon: '🛒',
      description: 'Consumer Packaged Goods',
      data: 'Sales data, inventory, consumer behavior, retail analytics',
      hint: 'Pre-configured with sample data to explore'
    },
    { 
      id: 'smart-buildings', 
      name: 'Smart Buildings', 
      icon: '🏢',
      description: 'Facility & IoT Management',
      data: 'Energy consumption, HVAC systems, occupancy sensors, maintenance logs',
      hint: 'Pre-configured with sample data to explore'
    },
  ];

  const allUseCases = [...defaultUseCases, ...customUseCases];

  const handleCreateSubscription = () => {
    if (newSubscriptionName.trim()) {
      const newUseCase = {
        id: `custom-${Date.now()}`,
        name: newSubscriptionName.trim(),
        icon: '✨',
        isCustom: true
      };
      setCustomUseCases([...customUseCases, newUseCase]);
      setSelectedUseCase(newUseCase.id);
      setNewSubscriptionName('');
      setShowCreateSubscriptionModal(false);
    }
  };

  const sampleQuestions = [
    "Build a Customer 360 data product",
    "Show me revenue trends by region",
    "Create a sales analytics dashboard",
    "What are my top performing products?",
    "Analyze customer churn patterns"
  ];

  const agents = {
    dataApps: [
      { id: 'data-products', name: 'Data Products', icon: '📦' },
      { id: 'dashboards', name: 'Dashboards', icon: '📊' },
      { id: 'chatbots', name: 'Chatbots', icon: '💬' },
      { id: 'ai-models', name: 'AI Models', icon: '🤖' },
    ],
    dataDiscovery: [
      { id: 'semantic-graph', name: 'Semantic Data Graph', icon: '🕸️' },
      { id: 'data-transformation', name: 'Data Transformation', icon: '🔄' },
      { id: 'data-analysis', name: 'Data Analysis', icon: '🔍' },
    ]
  };

  const models = [
    { id: 'gpt4', name: 'GPT-4o', provider: 'OpenAI' },
    { id: 'gpt4-turbo', name: 'GPT-4 Turbo', provider: 'OpenAI' },
    { id: 'claude', name: 'Claude 3.5', provider: 'Anthropic' },
    { id: 'gemini', name: 'Gemini Pro', provider: 'Google' },
  ];

  // Typing animation effect
  React.useEffect(() => {
    const currentQuestion = sampleQuestions[placeholderIndex];
    let charIndex = 0;
    let typingTimeout;
    let pauseTimeout;

    const typeChar = () => {
      if (charIndex <= currentQuestion.length) {
        setDisplayedPlaceholder(currentQuestion.slice(0, charIndex));
        charIndex++;
        typingTimeout = setTimeout(typeChar, 50);
      } else {
        // Pause at the end before moving to next question
        pauseTimeout = setTimeout(() => {
          setPlaceholderIndex((prev) => (prev + 1) % sampleQuestions.length);
        }, 2000);
      }
    };

    if (isTyping && !chatInput) {
      typeChar();
    }

    return () => {
      clearTimeout(typingTimeout);
      clearTimeout(pauseTimeout);
    };
  }, [placeholderIndex, isTyping, chatInput]);

  // Close dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => {
      setShowAgentDropdown(false);
      setShowModelDropdown(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (chatInput.trim()) {
      // Redirect to auth page with the pending query
      navigate('/auth', { state: { pendingQuery: chatInput } });
    }
  };

  const handleConnectorClick = (connector) => {
    setSelectedConnector(connector);
    setIsModalOpen(true);
    setConnectionSuccess(false);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleTestConnection = async (e) => {
    e.preventDefault();
    setTestingConnection(true);
    setConnectionSuccess(false);
    
    // Simulate API call to test connection
    // TODO: Replace with actual API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setTestingConnection(false);
    setConnectionSuccess(true);
    
    console.log('Connection tested:', formData);
    
    // Navigate to data connections page after success
    setTimeout(() => {
      setIsModalOpen(false);
      setConnectionSuccess(false);
      navigate('/app/connections');
    }, 1500);
  };
  const dataConnectors = [
    {
      name: 'Upload File',
      icon: 'https://cdn-icons-png.flaticon.com/128/3143/3143609.png',
      description: 'Upload CSV, Excel, or JSON files'
    },
    {
      name: 'Snowflake',
      icon: 'https://companieslogo.com/img/orig/SNOW-35164165.png',
      description: 'Connect to Snowflake data warehouse'
    },
    {
      name: 'Google Cloud Storage',
      icon: 'https://cdn.worldvectorlogo.com/logos/google-cloud-2.svg',
      description: 'Access data from GCS buckets'
    },
    {
      name: 'Azure Data Lake',
      icon: 'https://swimburger.net/media/ppnn3pcl/azure.png',
      description: 'Connect to ADLS Gen2 storage'
    },
    {
      name: 'Databricks',
      icon: 'https://asset.brandfetch.io/idDwd0nn9u/idm22uhHUb.png',
      description: 'Access Databricks tables and data'
    },
    {
      name: 'PostgreSQL',
      icon: 'https://cdn-icons-png.flaticon.com/128/5968/5968342.png',
      description: 'Connect to PostgreSQL databases'
    },
    {
      name: 'Amazon S3',
      icon: 'https://cdn.worldvectorlogo.com/logos/aws-s3.svg',
      description: 'Access data from S3 buckets'
    },
    {
      name: 'Azure Synapse',
      icon: 'https://swimburger.net/media/ppnn3pcl/azure.png',
      description: 'Connect to Azure Synapse Analytics'
    }
  ];

  return (
    <div className="min-h-screen relative">
      {/* Background */}
      <GridBackground />

      {/* Header */}
      <header className="relative z-20 bg-black/20 backdrop-blur-md">
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img src="/intugle-logo.svg" alt="Intugle" className="h-14 w-auto" />
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-400 hover:text-white transition-colors duration-300">Features</a>
              <a href="#connectors" className="text-gray-400 hover:text-white transition-colors duration-300">Connectors</a>
              <a href="#about" className="text-gray-400 hover:text-white transition-colors duration-300">About</a>
              <Button variant="ghost" className="text-gray-400 hover:text-gray-200 hover:bg-transparent transition-colors duration-300">
                Watch Demo
              </Button>
              <Button 
                onClick={() => navigate('/app/connections')}
                className="bg-blue-600 hover:bg-blue-700 text-white transition-all duration-300"
              >
                Launch App
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <div className="container mx-auto px-4 pt-24 pb-20">
          
          {/* Main Title Section */}
          <div className="text-center max-w-4xl mx-auto mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 leading-tight font-heading">
              Transform Your Data Into
              <span className="block mt-2 bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-500 bg-clip-text text-transparent">GenAI-Powered Apps</span>
            </h1>
            <p className="text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto">
              Build an intelligent semantic data graph once. Power infinite AI applications.
            </p>
          </div>

          {/* Chatbot Input Section */}
          <div className="max-w-3xl mx-auto mb-24">
            <div className="flex justify-center mb-5">
              <div className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 backdrop-blur-lg border border-blue-500/20 rounded-full px-5 py-2 inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-gray-300">Start vibing with your data</span>
              </div>
            </div>
            <form onSubmit={handleChatSubmit} className="relative">
              {/* Main Input Container */}
              <div className="rounded-xl border border-blue-500/10 shadow-[0_0_20px_rgba(59,130,246,0.25)] hover:shadow-[0_0_25px_rgba(59,130,246,0.35)] transition-all duration-200" style={{ backgroundColor: 'rgba(12, 18, 34, 0.85)' }}>
                {/* Input Area */}
                <div className="p-4 pb-2 relative">
                  <textarea
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onFocus={() => setIsTyping(false)}
                    onBlur={() => !chatInput && setIsTyping(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleChatSubmit(e);
                      }
                    }}
                    placeholder={displayedPlaceholder || "Ask anything..."}
                    rows={2}
                    className="w-full bg-transparent text-white text-base resize-none focus:outline-none placeholder:text-gray-500 leading-relaxed"
                    style={{ border: 'none', outline: 'none', boxShadow: 'none' }}
                  />
                  {/* Minimal footer line - workspace indicator (positioned at bottom of textarea area) */}
                  {(() => {
                    const selectedWorkspace = allUseCases.find(uc => uc.id === selectedUseCase);
                    if (selectedWorkspace && chatInput.trim()) {
                      return (
                        <p className="absolute bottom-5 left-4 text-[10px] text-gray-600/70 tracking-wide pointer-events-none">
                          ... using {selectedWorkspace.name} workspace with pre-configured data
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
                
                {/* Bottom Controls Bar */}
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2">
                    {/* Add Data Source Button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsModalOpen(true);
                      }}
                      className="w-8 h-8 rounded-full bg-slate-800/60 hover:bg-slate-700 flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200"
                      title="Add data source"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    
                    {/* Agent Selector */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAgentDropdown(!showAgentDropdown);
                          setShowModelDropdown(false);
                        }}
                        className="flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-slate-800/60 hover:bg-slate-700 transition-colors text-xs text-gray-300 hover:text-white"
                      >
                        <span className="text-[11px]">{selectedAgent.icon}</span>
                        <span>{selectedAgent.name}</span>
                        <ChevronUp className={`w-3 h-3 text-gray-500 transition-transform duration-200 ${showAgentDropdown ? '' : 'rotate-180'}`} />
                      </button>
                      
                      {/* Agent Dropdown */}
                      {showAgentDropdown && (
                        <div className="absolute bottom-full left-0 mb-2 w-56 bg-slate-900 border border-slate-700/50 rounded-xl shadow-2xl z-[100] overflow-hidden">
                          <div className="p-1.5">
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-2.5 py-1.5">Data Apps</p>
                            {agents.dataApps.map((agent) => (
                              <button
                                key={agent.id}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedAgent(agent);
                                  setShowAgentDropdown(false);
                                }}
                                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-800 transition-colors ${
                                  selectedAgent.id === agent.id ? 'bg-slate-800' : ''
                                }`}
                              >
                                <span className="text-sm">{agent.icon}</span>
                                <span className="text-xs font-medium text-white">{agent.name}</span>
                              </button>
                            ))}
                          </div>
                          
                          <div className="border-t border-slate-700/50 mx-1.5"></div>
                          
                          <div className="p-1.5">
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-2.5 py-1.5">Data Discovery</p>
                            {agents.dataDiscovery.map((agent) => (
                              <button
                                key={agent.id}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedAgent(agent);
                                  setShowAgentDropdown(false);
                                }}
                                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-800 transition-colors ${
                                  selectedAgent.id === agent.id ? 'bg-slate-800' : ''
                                }`}
                              >
                                <span className="text-sm">{agent.icon}</span>
                                <span className="text-xs font-medium text-white">{agent.name}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Model Selector */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowModelDropdown(!showModelDropdown);
                          setShowAgentDropdown(false);
                        }}
                        className="flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-slate-800/60 hover:bg-slate-700 transition-colors text-xs text-gray-300 hover:text-white"
                      >
                        <span>{selectedModel.name}</span>
                        <ChevronUp className={`w-3 h-3 text-gray-500 transition-transform duration-200 ${showModelDropdown ? '' : 'rotate-180'}`} />
                      </button>
                      
                      {/* Model Dropdown */}
                      {showModelDropdown && (
                        <div className="absolute bottom-full left-0 mb-2 w-48 bg-slate-900 border border-slate-700/50 rounded-xl shadow-2xl z-[100] overflow-hidden">
                          <div className="p-1.5">
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-2.5 py-1.5">Select Model</p>
                            {models.map((model) => (
                              <button
                                key={model.id}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedModel(model);
                                  setShowModelDropdown(false);
                                }}
                                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-800 transition-colors ${
                                  selectedModel.id === model.id ? 'bg-slate-800' : ''
                                }`}
                              >
                                <div className="text-left">
                                  <p className="text-xs font-medium text-white">{model.name}</p>
                                  <p className="text-[10px] text-gray-500">{model.provider}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Voice Button */}
                    <button
                      type="button"
                      className="w-8 h-8 rounded-full bg-slate-800/60 hover:bg-slate-700 flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200"
                      title="Voice input"
                    >
                      <Mic className="w-4 h-4" />
                    </button>
                    
                    {/* Send Button */}
                    <button
                      type="submit"
                      disabled={!chatInput.trim()}
                      className="w-8 h-8 rounded-full bg-slate-800/60 hover:bg-slate-700 disabled:bg-slate-800/60 disabled:cursor-not-allowed flex items-center justify-center text-gray-300 hover:text-white disabled:text-gray-500 transition-all duration-200"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Use Case Chips */}
                <div className="bg-slate-950/60 px-4 py-3 rounded-b-xl">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 font-medium">Try:</span>
                    {allUseCases.map((useCase) => (
                      <button
                        key={useCase.id}
                        type="button"
                        onClick={() => setSelectedUseCase(useCase.id)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                          selectedUseCase === useCase.id
                            ? 'bg-blue-600/25 text-blue-400'
                            : 'bg-slate-800/70 text-gray-400 hover:text-white hover:bg-slate-700/80'
                        }`}
                      >
                        <span>{useCase.icon}</span>
                        <span>{useCase.name}</span>
                      </button>
                    ))}
                    {/* Create New Workspace Button */}
                    <div className="relative group">
                      <button
                        type="button"
                        onClick={() => setShowCreateSubscriptionModal(true)}
                        className="w-7 h-7 rounded-full flex items-center justify-center text-blue-400 hover:text-white bg-blue-500/15 hover:bg-blue-500/30 transition-all duration-200"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      {/* Tooltip for + button */}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-slate-900 border border-slate-700/50 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                        <div className="p-3">
                          <p className="text-sm font-medium text-white mb-1">Create New Workspace</p>
                          <p className="text-xs text-gray-400">Set up a custom workspace for your data</p>
                        </div>
                        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-[1px]">
                          <div className="border-8 border-transparent border-t-slate-700/50"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Create Workspace Modal */}
          {showCreateSubscriptionModal && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100]" onClick={() => setShowCreateSubscriptionModal(false)}>
              <div 
                className="bg-slate-900 border border-slate-700/50 rounded-xl shadow-2xl w-full max-w-md mx-4"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/50">
                  <h3 className="text-lg font-semibold text-white">Create New Workspace</h3>
                  <button 
                    onClick={() => setShowCreateSubscriptionModal(false)}
                    className="p-1 text-gray-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-5">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Workspace Name
                  </label>
                  <input
                    type="text"
                    value={newSubscriptionName}
                    onChange={(e) => setNewSubscriptionName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCreateSubscription();
                      }
                    }}
                    placeholder="e.g., Finance Analytics, Retail Ops..."
                    className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    A workspace contains your data connections, sessions, and apps.
                  </p>
                </div>
                <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-700/50 bg-slate-950/50 rounded-b-xl">
                  <button
                    onClick={() => setShowCreateSubscriptionModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateSubscription}
                    disabled={!newSubscriptionName.trim()}
                    className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-gray-500 text-white rounded-lg transition-colors"
                  >
                    Create Workspace
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Steps - Numbered */}
          <div className="flex justify-center items-center gap-6 mb-12">
            {/* Step 1 */}
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-xs font-bold flex items-center justify-center">1</span>
              <span className="text-sm text-gray-300">Connect Data Sources</span>
            </div>
            
            <div className="flex items-center gap-1 text-blue-400/60">
              <div className="w-8 h-[1px] bg-gradient-to-r from-transparent to-blue-400/60"></div>
              <ArrowRight className="w-4 h-4" />
            </div>
            
            {/* Step 2 */}
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-xs font-bold flex items-center justify-center">2</span>
              <span className="text-sm text-gray-300">Build Semantic Graph</span>
            </div>
            
            <div className="flex items-center gap-1 text-blue-400/60">
              <div className="w-8 h-[1px] bg-gradient-to-r from-transparent to-blue-400/60"></div>
              <ArrowRight className="w-4 h-4" />
            </div>
            
            {/* Step 3 */}
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-xs font-bold flex items-center justify-center">3</span>
              <span className="text-sm text-gray-300">Power AI Apps</span>
            </div>
          </div>

          {/* Data Connectors Section */}
          <div id="connectors" className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Connect Your Data Sources
              </h2>
              <p className="text-gray-500 text-lg">
                Seamlessly integrate with your existing data infrastructure
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mx-auto" style={{maxWidth: '90%'}}>
              {dataConnectors.map((connector, index) => (
                <Card
                  key={index}
                  onClick={() => handleConnectorClick(connector)}
                  className="bg-white/[0.03] backdrop-blur-lg border-slate-800/50 hover:bg-white/[0.06] hover:border-blue-500/30 transition-all duration-300 hover:scale-105 cursor-pointer group p-6 relative"
                >
                  {/* Hover Arrow Indicator - Top Right */}
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ArrowRight className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="w-16 h-16 rounded-xl bg-slate-800/50 flex items-center justify-center group-hover:bg-slate-800/70 transition-all duration-300 border border-slate-700/30">
                      <img
                        src={connector.icon}
                        alt={connector.name}
                        className="w-10 h-10 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <Database className="w-10 h-10 text-slate-400 hidden" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg mb-2">
                        {connector.name}
                      </h3>
                      <p className="text-gray-500 text-sm">
                        {connector.description}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div id="features" className="container mx-auto px-4 py-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">
                Vibe Engineer Your Data Stack
              </h2>
              <p className="text-gray-500 text-lg">
                Everything you need to build intelligent data applications
              </p>
            </div>

            {/* New Layout: Semantic Graph on left + AI Agents on right */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Large Semantic Data Graph Box - Left Side */}
              <div className="bg-white/[0.03] backdrop-blur-lg border border-slate-800/50 rounded-xl overflow-hidden hover:bg-white/[0.06] transition-all duration-300 flex flex-col lg:aspect-square min-h-[400px] sm:min-h-[450px]">
                {/* Title above the graph */}
                <div className="p-3 sm:p-4 border-b border-slate-800/30">
                  <h3 className="text-lg sm:text-xl font-semibold text-white">Semantic Data Graph</h3>
                  <p className="text-gray-500 text-xs sm:text-sm mt-1">Semantic intelligence autonomously built on data, enabling LLMs with context.</p>
                </div>
                {/* Graph Animation Container */}
                <div className="flex-1 min-h-[320px] sm:min-h-[380px]">
                  <AnimatedSemanticGraph />
                </div>
              </div>

              {/* AI Agents Section - Right Side */}
              <div className="bg-white/[0.03] backdrop-blur-lg border border-slate-800/50 rounded-xl p-4 sm:p-5 hover:bg-white/[0.06] transition-all duration-300 lg:aspect-square flex flex-col min-h-[400px] sm:min-h-[450px]">
                {/* Section Header */}
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-white">AI-Powered Data Agents</h3>
                  <p className="text-gray-500 text-xs sm:text-sm mt-1">
                    Purpose-built AI agents that understand your semantic graph to transform and explore data using natural language.
                  </p>
                  
                  {/* Use Case Pills */}
                  <div className="mt-4 sm:mt-6">
                    <p className="text-[10px] sm:text-xs text-gray-500 mb-2">What you can do:</p>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {/* Data Transformation - Green */}
                      <span className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-green-500/10 text-[10px] sm:text-xs text-green-400 border border-green-500/30">Join Tables</span>
                      <span className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-green-500/10 text-[10px] sm:text-xs text-green-400 border border-green-500/30">Filter & Aggregate</span>
                      <span className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-green-500/10 text-[10px] sm:text-xs text-green-400 border border-green-500/30 hidden sm:inline-block">Build Pipelines</span>
                      <span className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-green-500/10 text-[10px] sm:text-xs text-green-400 border border-green-500/30 hidden sm:inline-block">Data Profiling</span>
                      {/* Data Analysis - Blue */}
                      <span className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-blue-500/10 text-[10px] sm:text-xs text-blue-400 border border-blue-500/30">Create Charts</span>
                      <span className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-blue-500/10 text-[10px] sm:text-xs text-blue-400 border border-blue-500/30 hidden sm:inline-block">Export Reports</span>
                      <span className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-blue-500/10 text-[10px] sm:text-xs text-blue-400 border border-blue-500/30 hidden sm:inline-block">Predict Trends</span>
                      <span className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-blue-500/10 text-[10px] sm:text-xs text-blue-400 border border-blue-500/30 hidden sm:inline-block">Schedule Jobs</span>
                    </div>
                  </div>
                </div>

                {/* Two Agent Boxes - Stack on mobile, side by side on larger screens */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-8">
                  {/* Data Transformation Agent */}
                  <div className="bg-slate-900/50 rounded-xl p-3 sm:p-4 transition-all duration-300 flex flex-col">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-6 sm:w-7 h-6 sm:h-7 rounded-md bg-green-500/20 border border-green-500/30 flex items-center justify-center flex-shrink-0">
                        <Shuffle className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-green-400" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-xs sm:text-sm font-semibold text-white">Data Transformation</h4>
                        <p className="text-gray-500 text-[9px] sm:text-[10px]">Intelligent code generation</p>
                      </div>
                    </div>
                    
                    <div className="bg-slate-800/30 rounded-lg p-3 flex-1 flex flex-col">
                      <p className="text-[8px] sm:text-[9px] text-gray-300 mb-2">"Join sales, products & customers, calculate revenue by category, YoY growth%"</p>
                      
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse flex-shrink-0"></div>
                        <span className="text-[8px] sm:text-[9px] text-green-400">Generating SQL...</span>
                      </div>
                      
                      <div className="rounded p-2 flex-1">
                        <code className="text-[6px] sm:text-[7px] text-gray-500 font-mono leading-relaxed block whitespace-pre">{`SELECT p.category,
  SUM(s.amount) as revenue,
  LAG(SUM(s.amount), 12) 
    OVER(ORDER BY month) as yoy,
  ROUND(100.0 * (SUM(s.amount) - 
    LAG(SUM(s.amount), 12) 
    OVER(ORDER BY month)) / 
    NULLIF(LAG(SUM(s.amount), 12) 
    OVER(ORDER BY month), 0), 2) as growth_pct
FROM sales s
JOIN products p ON s.id = p.id`}</code>
                      </div>
                    </div>
                  </div>

                  {/* Data Analysis Agent */}
                  <div className="bg-slate-900/50 rounded-xl p-3 sm:p-4 transition-all duration-300 flex flex-col">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-6 sm:w-7 h-6 sm:h-7 rounded-md bg-blue-500/20 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
                        <BarChart3 className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-blue-400" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-xs sm:text-sm font-semibold text-white">Data Analysis</h4>
                        <p className="text-gray-500 text-[9px] sm:text-[10px]">Instant visual insights</p>
                      </div>
                    </div>
                    
                    <div className="bg-slate-800/30 rounded-lg p-3 flex-1 flex flex-col">
                      <p className="text-[8px] sm:text-[9px] text-gray-300 mb-2">"What factors are leading to revenue decline?"</p>
                      
                      <div className="text-[7px] sm:text-[8px] text-gray-400 space-y-0.5 mb-2">
                        <p>• Product returns up <span className="text-blue-400">+23%</span></p>
                        <p>• Customer churn increased <span className="text-blue-400">+18%</span></p>
                        <p>• Avg order value down <span className="text-blue-400">-12%</span></p>
                      </div>
                      
                      {/* Chart Container */}
                      <div className="rounded flex-1 flex flex-col">
                        <p className="text-[7px] sm:text-[8px] text-gray-500 mb-1">Impact by Region</p>
                        {/* Legend */}
                        <div className="flex items-center gap-2 sm:gap-3 mb-1">
                          <div className="flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-sm bg-blue-500 flex-shrink-0"></div>
                            <span className="text-[6px] sm:text-[7px] text-gray-500">Returns</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-sm bg-blue-400/60 flex-shrink-0"></div>
                            <span className="text-[6px] sm:text-[7px] text-gray-500">Churn</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-sm bg-blue-300/50 flex-shrink-0"></div>
                            <span className="text-[6px] sm:text-[7px] text-gray-500">AOV</span>
                          </div>
                        </div>
                        
                        {/* Stacked Bar Chart */}
                        <div className="flex-1 flex flex-col justify-end">
                          <div className="flex items-end justify-between gap-2 sm:gap-3">
                            <div className="flex flex-col w-[14px] sm:w-[16px]"><div className="w-full bg-blue-500 h-[32px] rounded-t-sm"></div><div className="w-full bg-blue-400/60 h-[12px]"></div><div className="w-full bg-blue-300/50 h-[12px] rounded-b-sm"></div></div>
                            <div className="flex flex-col w-[14px] sm:w-[16px]"><div className="w-full bg-blue-500 h-[38px] rounded-t-sm"></div><div className="w-full bg-blue-400/60 h-[12px]"></div><div className="w-full bg-blue-300/50 h-[6px] rounded-b-sm"></div></div>
                            <div className="flex flex-col w-[14px] sm:w-[16px]"><div className="w-full bg-blue-500 h-[20px] rounded-t-sm"></div><div className="w-full bg-blue-400/60 h-[18px]"></div><div className="w-full bg-blue-300/50 h-[12px] rounded-b-sm"></div></div>
                            <div className="flex flex-col w-[14px] sm:w-[16px]"><div className="w-full bg-blue-500 h-[32px] rounded-t-sm"></div><div className="w-full bg-blue-400/60 h-[12px]"></div><div className="w-full bg-blue-300/50 h-[6px] rounded-b-sm"></div></div>
                            <div className="flex flex-col w-[14px] sm:w-[16px]"><div className="w-full bg-blue-500 h-[26px] rounded-t-sm"></div><div className="w-full bg-blue-400/60 h-[12px]"></div><div className="w-full bg-blue-300/50 h-[12px] rounded-b-sm"></div></div>
                            <div className="flex flex-col w-[14px] sm:w-[16px]"><div className="w-full bg-blue-500 h-[44px] rounded-t-sm"></div><div className="w-full bg-blue-400/60 h-[6px]"></div><div className="w-full bg-blue-300/50 h-[6px] rounded-b-sm"></div></div>
                          </div>
                          <div className="flex justify-between text-[5px] sm:text-[6px] text-gray-500 mt-1 px-0.5">
                            <span>US</span><span>UK</span><span>DE</span><span>FR</span><span>JP</span><span>AU</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Use Cases Section - Build Data Apps */}
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Build Intelligent Data Applications
              </h2>
              <p className="text-lg text-gray-400 max-w-3xl mx-auto">
                Leverage the intelligence from your semantic graph to power a variety of data applications
              </p>
            </div>

            {/* Three Use Case Boxes - Detailed like feature boxes */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Data Products - Customer 360 View */}
              <div className="bg-white/[0.03] backdrop-blur-lg border border-slate-800/50 rounded-xl p-4 hover:bg-white/[0.06] transition-all duration-300 flex flex-col">
                <h3 className="text-base font-semibold text-white mb-0.5">Data Products</h3>
                <p className="text-gray-500 text-[10px] mb-3">Select attributes, auto-generate unified views</p>
                <div className="bg-slate-900/50 rounded-lg p-3 flex-1 flex flex-col">
                  {/* Data Sources Header */}
                  <div className="flex items-center justify-center gap-1 mb-2">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-blue-500/40 to-transparent"></div>
                    <span className="text-[9px] text-blue-400 px-2 font-medium">RAW DATA</span>
                    <div className="h-px flex-1 bg-gradient-to-l from-transparent via-blue-500/40 to-transparent"></div>
                  </div>
                  
                  {/* Source Tables with Selectable Attributes */}
                  <div className="space-y-2.5 mb-3">
                    {/* CRM Table */}
                    <div className="bg-slate-800/50 rounded p-2">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <img src="https://cdn.worldvectorlogo.com/logos/google-cloud-2.svg" alt="GCS" className="w-3.5 h-3.5" />
                        <span className="text-[9px] text-gray-500">CRM</span>
                        <span className="text-[9px] text-gray-500">·</span>
                        <span className="text-[9px] text-gray-300 font-medium font-mono">customers</span>
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        <span className="text-[9px] px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30 font-mono">✓ cust_id</span>
                        <span className="text-[9px] px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30 font-mono">✓ name</span>
                        <span className="text-[9px] px-2 py-0.5 rounded bg-slate-700/30 text-gray-500 border border-slate-600/30 font-mono">email</span>
                        <span className="text-[9px] px-2 py-0.5 rounded bg-slate-700/30 text-gray-500 border border-slate-600/30 font-mono">ph</span>
                      </div>
                    </div>
                    {/* ERP Table */}
                    <div className="bg-slate-800/50 rounded p-2">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <img src="https://swimburger.net/media/ppnn3pcl/azure.png" alt="Azure" className="w-3.5 h-3.5" />
                        <span className="text-[9px] text-gray-500">ERP</span>
                        <span className="text-[9px] text-gray-500">·</span>
                        <span className="text-[9px] text-gray-300 font-medium font-mono">sales_orders</span>
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        <span className="text-[9px] px-2 py-0.5 rounded bg-green-500/20 text-green-400 border border-green-500/30 font-mono">✓ total_val</span>
                        <span className="text-[9px] px-2 py-0.5 rounded bg-green-500/20 text-green-400 border border-green-500/30 font-mono">✓ ord_cnt</span>
                        <span className="text-[9px] px-2 py-0.5 rounded bg-slate-700/30 text-gray-500 border border-slate-600/30 font-mono">status</span>
                      </div>
                    </div>
                    {/* Service Requests Table */}
                    <div className="bg-slate-800/50 rounded p-2">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <img src="https://cdn-icons-png.flaticon.com/128/5968/5968342.png" alt="PostgreSQL" className="w-3.5 h-3.5" />
                        <span className="text-[9px] text-gray-500">SVC</span>
                        <span className="text-[9px] text-gray-500">·</span>
                        <span className="text-[9px] text-gray-300 font-medium font-mono">service_requests</span>
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        <span className="text-[9px] px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 border border-purple-500/30 font-mono">✓ tickets</span>
                        <span className="text-[9px] px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 border border-purple-500/30 font-mono">✓ nps</span>
                        <span className="text-[9px] px-2 py-0.5 rounded bg-slate-700/30 text-gray-500 border border-slate-600/30 font-mono">priority</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Federated Query */}
                  <div className="flex items-center justify-center gap-1 mb-2">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-blue-500/40 to-transparent"></div>
                    <span className="text-[9px] text-blue-400 px-2 font-medium">FEDERATED QUERY</span>
                    <div className="h-px flex-1 bg-gradient-to-l from-transparent via-blue-500/40 to-transparent"></div>
                  </div>
                  
                  {/* Sample Query */}
                  <div className="bg-slate-950/50 rounded p-2 mb-3 border border-slate-700/30">
                    <code className="text-[9px] text-gray-400 font-mono leading-relaxed block">
                      <span className="text-blue-400">SELECT</span> c.id, <span className="text-green-400">SUM</span>(o.val)<br/>
                      <span className="text-blue-400">FROM</span> crm.cust c <span className="text-blue-400">JOIN</span> erp.ord<br/>
                      <span className="text-blue-400">ON</span> c.id = o.cust_id
                    </code>
                  </div>
                  
                  {/* Output Header */}
                  <div className="flex items-center justify-center gap-1 mb-2">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-blue-500/40 to-transparent"></div>
                    <span className="text-[9px] text-blue-400 px-2 font-medium">DATA PRODUCT</span>
                    <div className="h-px flex-1 bg-gradient-to-l from-transparent via-blue-500/40 to-transparent"></div>
                  </div>
                  
                  {/* Unified Output */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2 mt-auto">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[9px] text-blue-400 font-medium">Customer 360</span>
                      <span className="text-[9px] text-green-400 bg-green-500/20 px-2 py-0.5 rounded">Ready</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1">
                        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-[8px] text-gray-400">Schedule</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="text-[8px] text-gray-400">Access</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        <span className="text-[8px] text-gray-400">Integrate</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Chatbots - Two Panel Layout */}
              <div className="bg-white/[0.03] backdrop-blur-lg border border-slate-800/50 rounded-xl p-4 hover:bg-white/[0.06] transition-all duration-300 flex flex-col">
                <h3 className="text-base font-semibold text-white mb-0.5">Chatbots</h3>
                <p className="text-gray-500 text-[10px] mb-3">Configure & preview your analytics chatbot</p>
                
                {/* Two Panel Container */}
                <div className="flex gap-2 flex-1">
                  {/* Left Panel - Configuration */}
                  <div className="w-1/2 bg-slate-900/50 rounded-lg p-2.5 flex flex-col">
                    <span className="text-[9px] text-gray-400 mb-2 font-medium">Configuration</span>
                    
                    {/* Config Items with Summary */}
                    <div className="space-y-1.5 flex-1">
                      {/* Source */}
                      <div className="flex items-center justify-between px-2.5 py-2 rounded bg-slate-800/50">
                        <div className="flex items-center gap-2">
                          <Database className="w-3 h-3 text-blue-400" />
                          <span className="text-[9px] text-gray-300">Source</span>
                        </div>
                        <span className="text-[8px] text-blue-400">15 tables</span>
                      </div>
                      
                      {/* KPIs */}
                      <div className="flex items-center justify-between px-2.5 py-2 rounded bg-slate-800/50">
                        <div className="flex items-center gap-2">
                          <Target className="w-3 h-3 text-purple-400" />
                          <span className="text-[9px] text-gray-300">KPIs</span>
                        </div>
                        <span className="text-[8px] text-purple-400">23 defined</span>
                      </div>
                      
                      {/* Instructions */}
                      <div className="flex items-center justify-between px-2.5 py-2 rounded bg-slate-800/50">
                        <div className="flex items-center gap-2">
                          <FileText className="w-3 h-3 text-orange-400" />
                          <span className="text-[9px] text-gray-300">Instructions</span>
                        </div>
                        <span className="text-[8px] text-orange-400">7 enabled</span>
                      </div>
                      
                      {/* Tools - Expanded with ON/OFF */}
                      <div className="px-2.5 py-2 rounded bg-slate-800/50">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-3 h-3 text-green-400" />
                            <span className="text-[9px] text-gray-300">Tools</span>
                          </div>
                          <span className="text-[8px] text-green-400">4 active</span>
                        </div>
                        <div className="space-y-1 ml-1 pl-[14px] border-l border-gray-700/70">
                          <div className="flex items-center justify-between">
                            <span className="text-[8px] text-gray-400">Web Search</span>
                            <span className="text-[7px] text-gray-500 font-mono">[ON]</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[8px] text-gray-400">Visualization</span>
                            <span className="text-[7px] text-gray-500 font-mono">[ON]</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[8px] text-gray-600">Unstructured</span>
                            <span className="text-[7px] text-gray-600 font-mono">[OFF]</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[8px] text-gray-400">Autocorrection</span>
                            <span className="text-[7px] text-gray-500 font-mono">[ON]</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[8px] text-gray-400">Fewshot</span>
                            <span className="text-[7px] text-gray-500 font-mono">[ON]</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Integration */}
                      <div className="flex items-center justify-between px-2.5 py-2 rounded bg-slate-800/50">
                        <div className="flex items-center gap-2">
                          <Network className="w-3 h-3 text-cyan-400" />
                          <span className="text-[9px] text-gray-300">Integration</span>
                        </div>
                        <span className="text-[8px] text-cyan-400 font-mono">MCP</span>
                      </div>
                      
                      {/* Access */}
                      <div className="flex items-center justify-between px-2.5 py-2 rounded bg-slate-800/50">
                        <div className="flex items-center gap-2">
                          <Users className="w-3 h-3 text-pink-400" />
                          <span className="text-[9px] text-gray-300">Access</span>
                        </div>
                        <span className="text-[8px] text-pink-400">48 users</span>
                      </div>
                      
                      {/* LLM Model */}
                      <div className="flex items-center justify-between px-2.5 py-2 rounded bg-slate-800/50">
                        <div className="flex items-center gap-2">
                          <Bot className="w-3 h-3 text-yellow-400" />
                          <span className="text-[9px] text-gray-300">LLM Model</span>
                        </div>
                        <span className="text-[8px] text-yellow-400 font-mono">GPT 5.2</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right Panel - Chat Preview */}
                  <div className="w-1/2 bg-slate-900/50 rounded-lg p-2.5 flex flex-col">
                    <span className="text-[9px] text-gray-400 mb-2 font-medium">Preview</span>
                    
                    {/* Chat Output Area */}
                    <div className="flex-1 rounded space-y-1.5 min-h-[100px] overflow-hidden">
                      {/* User Message */}
                      <div className="flex justify-end mb-2">
                        <div className="bg-blue-600/20 border border-blue-500/30 rounded-lg px-2 py-1.5 max-w-[80%] flex items-center">
                          <span className="text-[9px] text-blue-300 leading-none">Summarize Q4 business performance</span>
                        </div>
                      </div>
                      
                      {/* AI Response */}
                      <div className="flex flex-col justify-start">
                        {/* Execution Steps */}
                        <div className="text-[8px] text-gray-500 space-y-0.5 mb-3">
                          <div className="flex items-center gap-1">
                            <Check className="w-2.5 h-2.5 text-green-400" />
                            <span>Creating analysis plan</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Check className="w-2.5 h-2.5 text-green-400" />
                            <span>Identifying tables & attr</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Check className="w-2.5 h-2.5 text-green-400" />
                            <span>Gathering context</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Check className="w-2.5 h-2.5 text-green-400" />
                            <span>Generating query</span>
                          </div>
                        </div>
                        {/* Response */}
                        <div className="bg-slate-800/50 rounded-lg px-2 py-1.5 max-w-[90%]">
                          <div className="space-y-1.5">
                            <p className="text-[9px] text-gray-300 font-medium">Q4 Highlights:</p>
                            <div className="text-[8px] text-gray-400 space-y-0.5">
                              <p>• Rev: <span className="text-blue-400">$8.2M</span> (+15% YoY)</p>
                              <p>• New customers: <span className="text-blue-400">2,340</span></p>
                              <p>• Top product: <span className="text-blue-400">Widget Pro</span></p>
                              <p>• Best region: <span className="text-blue-400">APAC</span></p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Chat Input */}
                    <div className="flex items-center gap-1.5 bg-slate-800/50 rounded-lg px-2 py-1.5">
                      <input 
                        type="text" 
                        placeholder="Ask anything ..." 
                        className="flex-1 bg-transparent text-[8px] text-gray-300 placeholder-gray-500 outline-none"
                        disabled
                      />
                      <ArrowUp className="w-3 h-3 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Models - ML Models */}
              <div className="bg-white/[0.03] backdrop-blur-lg border border-slate-800/50 rounded-xl p-4 hover:bg-white/[0.06] transition-all duration-300 flex flex-col">
                <h3 className="text-base font-semibold text-white mb-0.5">AI Models</h3>
                <p className="text-gray-500 text-[10px] mb-3">Build models with natural language</p>
                <div className="flex flex-col flex-1">
                  {/* Model Types */}
                  <div className="bg-slate-900/50 rounded-lg p-2.5">
                    <div className="space-y-2 divide-y divide-slate-800/40">
                      {/* Forecast - Collapsed */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded bg-blue-500/20 flex items-center justify-center">
                            <svg className="w-2.5 h-2.5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </div>
                          <span className="text-[9px] text-gray-300">Forecast</span>
                        </div>
                        <span className="text-[8px] text-blue-400">Revenue Q1</span>
                      </div>
                      {/* Causal - Collapsed */}
                      <div className="flex items-center justify-between pt-2">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded bg-purple-500/20 flex items-center justify-center">
                            <svg className="w-2.5 h-2.5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                            </svg>
                          </div>
                          <span className="text-[9px] text-gray-300">Causal</span>
                        </div>
                        <span className="text-[8px] text-purple-400">Campaign +18%</span>
                      </div>
                      {/* Anomaly - Collapsed */}
                      <div className="flex items-center justify-between pt-2">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded bg-red-500/20 flex items-center justify-center">
                            <svg className="w-2.5 h-2.5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                          <span className="text-[9px] text-gray-300">Anomaly</span>
                        </div>
                        <span className="text-[8px] text-red-400">3 alerts</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Prediction - Expanded Response - Pushed to bottom */}
                  <div className="bg-slate-900/50 rounded-lg p-3 mt-auto">
                    <div className="flex items-center gap-1.5 mb-2">
                      <div className="w-4 h-4 rounded bg-amber-500/20 flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      </div>
                      <span className="text-[9px] text-gray-300 font-medium">Prediction</span>
                    </div>
                    
                    {/* User Message */}
                    <div className="flex justify-end mb-3">
                      <div className="bg-blue-600/20 border border-blue-500/30 rounded-lg px-2.5 py-2 max-w-[75%] flex items-center">
                        <span className="text-[9px] text-blue-300 leading-tight">Build a churn prediction model for customers in NoAM region for next 90 days</span>
                      </div>
                    </div>
                    
                    {/* Execution Steps */}
                    <div className="text-[8px] text-gray-400 space-y-1.5 mb-3">
                      <div className="flex items-center gap-1.5">
                        <span className="w-3 h-3 text-[8px] text-gray-500 flex items-center justify-center flex-shrink-0">1.</span>
                        <span>Data preparation: <span className="text-gray-600 font-mono">3 tables | 12.4K rows | NoAM</span></span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-3 h-3 text-[8px] text-gray-500 flex items-center justify-center flex-shrink-0">2.</span>
                        <span>Feature engineering: <span className="text-gray-600 font-mono">23 features | tenure | usage</span></span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-3 h-3 text-[8px] text-gray-500 flex items-center justify-center flex-shrink-0">3.</span>
                        <span>Model training: <span className="text-gray-600 font-mono">XGBoost | 80/20 | 5-fold CV</span></span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-3 h-3 text-[8px] text-gray-500 flex items-center justify-center flex-shrink-0">4.</span>
                        <span>Validation: <span className="text-gray-600 font-mono">holdout | calibrated</span></span>
                      </div>
                    </div>
                    
                    {/* Model Output */}
                    <div className="bg-slate-800/50 rounded-lg px-2.5 py-2 mb-2 w-fit">
                      <p className="text-[9px] text-gray-300 font-medium mb-1">Model Ready:</p>
                      <div className="text-[8px] text-gray-400 space-y-0.5">
                        <p>AUC: <span className="text-green-400 font-mono">0.94</span> | Precision: <span className="text-green-400 font-mono">89%</span> | Recall: <span className="text-green-400 font-mono">82%</span></p>
                        <p>Top predictors: <span className="font-mono">tenure, usage_decline, support_tickets</span></p>
                        <p>High-risk customers: <span className="text-amber-400 font-mono">234</span></p>
                      </div>
                    </div>
                    
                    {/* User Input Box */}
                    <div className="flex items-center gap-1.5 bg-slate-800/50 rounded-lg px-2.5 py-2">
                      <input 
                        type="text" 
                        placeholder="Ask anything ..." 
                        className="flex-1 bg-transparent text-[8px] text-gray-300 placeholder-gray-500 outline-none"
                        disabled
                      />
                      <ArrowUp className="w-3.5 h-3.5 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Impact/Value Prop Section */}
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">
                Measurable Impact
              </h2>
              <p className="text-gray-500 text-lg">
                Real results from enterprises transforming their data operations
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* 90% Contextual Accuracy */}
              <div className="bg-white/[0.03] backdrop-blur-lg border border-slate-800/50 rounded-xl px-6 py-6 lg:py-10 hover:bg-white/[0.06] hover:border-blue-500/30 transition-all duration-300 group lg:aspect-square flex flex-col justify-start">
                <div className="mb-4 lg:mb-5">
                  <AnimatedCounter end={90} suffix="%" icon={Target} delay={0} duration={1000} />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2 lg:mb-3">Contextual Accuracy</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  AI-powered semantic understanding delivers precise, context-aware insights from your enterprise data.
                </p>
              </div>

              {/* 10x Hyperacceleration */}
              <div className="bg-white/[0.03] backdrop-blur-lg border border-slate-800/50 rounded-xl px-6 py-6 lg:py-10 hover:bg-white/[0.06] hover:border-blue-500/30 transition-all duration-300 group lg:aspect-square flex flex-col justify-start">
                <div className="mb-4 lg:mb-5">
                  <AnimatedCounter end={10} suffix="x" icon={Rocket} delay={1500} duration={1000} />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2 lg:mb-3">Hyperacceleration</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Automate data preparation and modeling tasks that used to take weeks into hours.
                </p>
              </div>

              {/* 50% Cost Savings */}
              <div className="bg-white/[0.03] backdrop-blur-lg border border-slate-800/50 rounded-xl px-6 py-6 lg:py-10 hover:bg-white/[0.06] hover:border-blue-500/30 transition-all duration-300 group lg:aspect-square flex flex-col justify-start">
                <div className="mb-4 lg:mb-5">
                  <AnimatedCounter end={50} suffix="%" icon={DollarSign} delay={3000} duration={1000} />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2 lg:mb-3">Cost Savings</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Cut your data engineering spend in half with automated pipelines and intelligent resource management.
                </p>
              </div>

              {/* 5x Data Democratization */}
              <div className="bg-white/[0.03] backdrop-blur-lg border border-slate-800/50 rounded-xl px-6 py-6 lg:py-10 hover:bg-white/[0.06] hover:border-blue-500/30 transition-all duration-300 group lg:aspect-square flex flex-col justify-start">
                <div className="mb-4 lg:mb-5">
                  <AnimatedCounter end={5} suffix="x" icon={Users} delay={4500} duration={1000} />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2 lg:mb-3">Data Democratization</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Enable business users to generate deeper insights from data without technical expertise.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white/[0.03] backdrop-blur-lg border border-slate-800/50 rounded-2xl p-12">
              <h2 className="text-4xl font-bold text-white mb-6 font-heading">
                Ready to Transform Your Data?
              </h2>
              <p className="text-xl text-gray-400 mb-8">
                Join leading data teams using Intugle to build intelligent data applications
              </p>
              <Button size="lg" className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-10 py-6 text-lg rounded-lg transition-all duration-300 shadow-lg hover:shadow-blue-500/25 hover:scale-105">
                Start Building Now
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-20 bg-black/20 backdrop-blur-md mt-20">
        <div className="container mx-auto px-4 py-12">
          {/* Main Footer Content - 4 Balanced Columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            
            {/* Column 1: Logo & Tagline */}
            <div>
              <img src="/intugle-logo.svg" alt="Intugle" className="h-16 w-auto mb-4" />
              <p className="text-gray-400 text-sm leading-relaxed">
                Agentic AI for Data Teams - The Data Platform Built for AI-First Enterprises
              </p>
            </div>

            {/* Column 2: Our Offices (Combined) */}
            <div>
              <h3 className="text-white font-semibold text-lg mb-4">Our Offices</h3>
              
              {/* Delaware HQ */}
              <div className="mb-4">
                <p className="text-gray-300 text-sm font-medium">Delaware, USA (HQ)</p>
                <p className="text-gray-500 text-sm">Intugle Inc.</p>
                <p className="text-gray-500 text-sm">8 The Green, Suite B, Dover, DE-19901</p>
              </div>
              
              {/* Bangalore */}
              <div>
                <p className="text-gray-300 text-sm font-medium">Bangalore, India</p>
                <p className="text-gray-500 text-sm">Intugle Pvt. Ltd.</p>
                <p className="text-gray-500 text-sm">2nd floor, no 2729, 16th cross, 27th main road, HSR layout sector 1, Bangalore - 560102</p>
              </div>
            </div>

            {/* Column 3: Resources & Community (Combined) */}
            <div>
              <h3 className="text-white font-semibold text-lg mb-4">Resources</h3>
              <ul className="space-y-2 mb-6">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">Documentation</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">Compliance & Trust Center</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">Privacy Policy</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">Terms of Service</a></li>
              </ul>
              
              <h3 className="text-white font-semibold text-lg mb-4">Community</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">GitHub</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">Discord Community</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">MCP Server</a></li>
              </ul>
            </div>

            {/* Column 4: Contact Us + Social */}
            <div>
              <h3 className="text-white font-semibold text-lg mb-4">Contact Us</h3>
              <div className="space-y-2 mb-6">
                <a href="mailto:hello@intugle.ai" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  hello@intugle.ai
                </a>
                <a href="tel:+14152379656" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  +1 (415) 237-9656 (US)
                </a>
                <a href="tel:+917829811923" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  +91 7829811923 (India)
                </a>
                <a href="tel:+13689932225" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  +1 (368) 993-2225 (Canada)
                </a>
              </div>
              
              <h3 className="text-white font-semibold text-lg mb-4">Follow Us</h3>
              <div className="flex items-center gap-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors" aria-label="LinkedIn">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors" aria-label="Twitter">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors" aria-label="YouTube">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors" aria-label="Discord">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors" aria-label="GitHub">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Divider & Copyright */}
          <div className="border-t border-slate-800 mt-12 pt-8">
            <p className="text-gray-500 text-sm text-center">
              © 2026 Intugle. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Configuration Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-slate-950/95 backdrop-blur-xl border-slate-700/50 text-white max-w-xl rounded-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3">
              {selectedConnector && (
                <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                  <img 
                    src={selectedConnector.icon} 
                    alt={selectedConnector.name}
                    className="w-6 h-6 object-contain"
                  />
                </div>
              )}
              <div>
                <DialogTitle className="text-lg font-semibold text-white">
                  Configure {selectedConnector?.name} Connection
                </DialogTitle>
                <DialogDescription className="text-gray-400 text-sm">
                  Enter your connection details to connect to {selectedConnector?.name}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <form onSubmit={handleTestConnection} className="mt-4">
            {/* Connection Name - Full Width */}
            <div className="mb-4">
              <Label htmlFor="connectionName" className="text-gray-300 font-medium text-sm">Connection Name</Label>
              <Input
                id="connectionName"
                name="connectionName"
                value={formData.connectionName}
                onChange={handleInputChange}
                placeholder="My Connection"
                className="mt-1.5 bg-slate-800/60 border border-transparent text-white placeholder:text-gray-500 h-10 focus:border-blue-500/50 focus:ring-0"
                required
              />
            </div>

            {/* Divider */}
            <div className="border-t border-slate-700/50 my-4"></div>

            {/* Section Header */}
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Connection Details</p>

            {/* Organization Name & Account Name */}
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <Label htmlFor="organizationName" className="text-gray-300 font-medium text-sm">Organization Name</Label>
                <Input
                  id="organizationName"
                  name="organizationName"
                  value={formData.organizationName}
                  onChange={handleInputChange}
                  placeholder="my-org"
                  className="mt-1.5 bg-slate-800/60 border border-transparent text-white placeholder:text-gray-500 h-10 focus:border-blue-500/50 focus:ring-0"
                  required
                />
              </div>
              <div>
                <Label htmlFor="accountName" className="text-gray-300 font-medium text-sm">Account Name</Label>
                <Input
                  id="accountName"
                  name="accountName"
                  value={formData.accountName}
                  onChange={handleInputChange}
                  placeholder="account-name"
                  className="mt-1.5 bg-slate-800/60 border border-transparent text-white placeholder:text-gray-500 h-10 focus:border-blue-500/50 focus:ring-0"
                  required
                />
              </div>
            </div>

            {/* Warehouse & Database */}
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <Label htmlFor="warehouse" className="text-gray-300 font-medium text-sm">Warehouse</Label>
                <Input
                  id="warehouse"
                  name="warehouse"
                  value={formData.warehouse}
                  onChange={handleInputChange}
                  placeholder="COMPUTE_WH"
                  className="mt-1.5 bg-slate-800/60 border border-transparent text-white placeholder:text-gray-500 h-10 focus:border-blue-500/50 focus:ring-0"
                  required
                />
              </div>
              <div>
                <Label htmlFor="database" className="text-gray-300 font-medium text-sm">Database</Label>
                <Input
                  id="database"
                  name="database"
                  value={formData.database}
                  onChange={handleInputChange}
                  placeholder="MY_DATABASE"
                  className="mt-1.5 bg-slate-800/60 border border-transparent text-white placeholder:text-gray-500 h-10 focus:border-blue-500/50 focus:ring-0"
                  required
                />
              </div>
            </div>

            {/* User - Half Width */}
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <Label htmlFor="user" className="text-gray-300 font-medium text-sm">User</Label>
                <Input
                  id="user"
                  name="user"
                  value={formData.user}
                  onChange={handleInputChange}
                  placeholder="username"
                  className="mt-1.5 bg-slate-800/60 border border-transparent text-white placeholder:text-gray-500 h-10 focus:border-blue-500/50 focus:ring-0"
                  required
                />
              </div>
            </div>

            {/* Password - Full Width */}
            <div className="mb-4">
              <Label htmlFor="password" className="text-gray-300 font-medium text-sm">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="••••••••"
                className="mt-1.5 bg-slate-800/60 border border-transparent text-white placeholder:text-gray-500 h-10 focus:border-blue-500/50 focus:ring-0"
                required
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsModalOpen(false)}
                className="bg-slate-800/60 border-0 text-gray-300 hover:bg-white hover:text-slate-900 h-10 px-5"
                disabled={testingConnection}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={testingConnection || connectionSuccess}
                className={`h-10 px-5 ${
                  connectionSuccess 
                    ? 'bg-green-600 hover:bg-green-600' 
                    : 'bg-blue-600 hover:bg-blue-700'
                } text-white`}
              >
                {testingConnection ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : connectionSuccess ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Connected!
                  </>
                ) : (
                  'Test Connection'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;